import React, { useState, useRef, useEffect } from 'react';

import './Login.css';
import { useDispatch } from 'react-redux';
import { clearTokens, setTokens } from '../../store/tokensSlice'; // Import akcji Redux
import { useNavigate } from 'react-router-dom';
import { ReactComponent as MapenLogo } from '../icons/logo.svg';
import ApiService from '../api_services/apiService.jsx'; // Import ApiService
import OrganizerRegistration from './organizerRegistration.jsx';
import { showAlert } from '../components/global_alert/alertBus';

function Login() {
  const [showLogin, setShowLogin] = useState(true);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showApprovalPending, setShowApprovalPending] = useState(false);
  const [isEmailChecked, setIsEmailChecked] = useState(false);
  const [isEmailExists, setIsEmailExists] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

    const passwordInputRef = useRef(null);

    // Walidacja hasła jak na backendzie:
    // min 8 znaków, 1 wielka litera, min 1 cyfra, min 1 znak specjalny z podanego zbioru
    const PASSWORD_REGEX =
        /^(?=.*[A-Z])(?=(?:.*\d){1,})(?=.*[!@#$%^&*()_+=?<>{}[\]-]).{8,}$/;

    const validatePasswordForRegistration = (value) => PASSWORD_REGEX.test(value);

    const isRegistrationPasswordContext = isEmailChecked && !isEmailExists;
    const isPasswordTouched = password && password.length > 0;
    const isPasswordValidForRegistration =
        isRegistrationPasswordContext && validatePasswordForRegistration(password);
    const isPasswordHasNumber = /\d/.test(password);
    const isPasswordHasSpecialChar = /[!@#$%^&*()_+=?<>{}[\]-]/.test(password);
    const isPasswordHasUpperCase = /[A-Z]/.test(password);

    const isPasswordHasMinLength = password && password.length >= 8;

    const [isModalOpen, setIsModalOpen] = useState(false);

    const toggleModal = (e) => {
        e.preventDefault(); // Zapobiega przeładowaniu strony
        e.stopPropagation(); // Zapobiega przypadkowemu zaznaczeniu checkboxa przy kliku w link
        setIsModalOpen(true);
    };


    const [termsAccepted, setTermsAccepted] = useState(false);
    const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);

    console.log('isEmailChecked:', isEmailChecked, 'username:', username);

    // useEffect do focusowania na polu hasła po sprawdzeniu emaila
    useEffect(() => {
        if (isEmailChecked && passwordInputRef.current) {
            passwordInputRef.current.focus();
        }
    }, [isEmailChecked]);

    // Funkcja obsługująca zakończenie rejestracji
    const saveTokens = (data) => {
        dispatch(setTokens({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            userEmail: data.userEmail,
            userPhoneNumber: data.phoneNumber,
            accessTokenExpiration: data.accessTokenExpiresAt,
            refreshTokenExpiration: data.refreshTokenExpiresAt,
            userRoles: data.userRoles,
        }));
    };

    const handleRegistrationComplete = () => {
        // Ustawienie email i hasła z rejestracji
        dispatch(clearTokens());
        setPassword('');
        setShowRegistration(false);
        setShowLogin(true);
        setIsEmailChecked(false);
        showAlert('Prawie gotowe!\nPotwierdź swój adres e-mail, a następnie zajmiemy się weryfikacją Twojego konta organizatora.\nDamy Ci znać mailowo, gdy wszystko będzie gotowe.\n\nSprawdzamy zgłoszenia na bieżąco, więc weryfikacja nie powinna zająć dużo czasu.');
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // KROK 1: Sprawdzenie czy email istnieje
            if (!isEmailChecked) {
                if (!username.trim()) {
                    showAlert('Wprowadź adres e-mail');
                    return;
                }

                // Sprawdzenie czy email istnieje w systemie
                const checkEmailResponse = await ApiService.get('/api/auth/is-email-exists?email=' + username);

                console.log('Odpowiedź sprawdzenia emaila:', checkEmailResponse);

                // Ustawienie stanów na podstawie odpowiedzi
                setIsEmailChecked(true);
                setIsEmailExists(checkEmailResponse.data.isAccountExists || false);

                return; // Zakończenie dla kroku 1
            }

            // KROK 2: Logowanie istniejącego użytkownika
            if (isEmailChecked && isEmailExists) {
                if (!password.trim()) {
                    showAlert('Wprowadź hasło');
                    return;
                }

                let response;
                try {
                    response = await ApiService.post('/api/auth/login/organiser', {
                        email: username,
                        password: password,
                    });
                } catch (error) {
                    const code = error.response?.data?.code;

                    if (code === 'ORGANIZER_ROLE_REQUIRED') {
                        // Użytkownik podał hasło tylko raz. Pobieramy w tle sesję USER,
                        // która jest wymagana do utworzenia profilu organizatora.
                        try {
                            const userLoginResponse = await ApiService.post('/api/auth/login', {
                                email: username,
                                password: password,
                            });

                            if (userLoginResponse.data?.accessToken) {
                                saveTokens(userLoginResponse.data);
                            }
                        } catch (userLoginErr) {
                            console.warn('Nie udało się pobrać sesji USER w tle:', userLoginErr);
                        }

                        showAlert('Twoje konto użytkownika nie posiada jeszcze profilu firmy. Uzupełnij poniższy formularz, aby złożyć wniosek.');
                        setShowLogin(false);
                        setShowRegistration(true);
                        return;
                    }

                    if (code === 'ORGANIZER_APPROVAL_REQUIRED') {
                        setShowLogin(false);
                        setShowApprovalPending(true);
                        return;
                    }

                    throw error;
                }

                if (response.data?.status === 'UNAUTHORIZED' && response.data?.message === 'User is not activated.') {
                    showAlert('❌ Twoje konto nie zostało aktywowane. Sprawdź swoją skrzynkę e-mail i kliknij link aktywacyjny.');
                    return;
                }

                console.log('Odpowiedź z API logowania:', response);

                if (response.data?.userRoles?.includes('EVENT_ADMIN')) {
                    saveTokens(response.data);

                    // Przekierowanie po udanym logowaniu
                    navigate('/');

                } else if (response.data) {
                    showAlert('Nie posiadasz uprawnień administratora wydarzeń (rola EVENT_ADMIN). Brak dostępu do systemu.');
                } else {
                    showAlert('Nieprawidłowa odpowiedź z serwera.');
                }

                return; // Zakończenie dla kroku 2
            }

            // KROK 3: Rejestracja nowego użytkownika
            if (isEmailChecked && !isEmailExists) {

                if (!password.trim()) {
                    showAlert('Wprowadź hasło');
                    return;
                }

                if (!validatePasswordForRegistration(password)) {
                    showAlert(
                        'Hasło musi mieć min. 8 znaków, 1 wielką literę, min. 1 cyfrę oraz min. 1 znak specjalny (!, @, #, $, %, ^, &, *, (, ), _, +, =, ?, <, >, {, }, [, ], -).'
                    );
                    return;
                }

                showAlert('Konto organizatora wymaga istniejącego konta użytkownika. Załóż je w aplikacji MAPEN, a następnie wróć tutaj.');
            }

        } catch (error) {
            console.error('Błąd podczas przetwarzania:', error);

            // Różne komunikaty błędów dla różnych kroków
            if (!isEmailChecked) {
                showAlert('Nie udało się sprawdzić adresu e-mail. Spróbuj ponownie.');
            } else if (isEmailExists) {
                if (error.response?.status === 401 || error.response?.status === 400) {
                    showAlert('Nieprawidłowy adres e-mail lub hasło.');
                } else {
                    showAlert(error.response?.data?.message || 'Nie udało się zalogować. Sprawdź swoje dane logowania.');
                }
            } else {
                showAlert('Nie udało się utworzyć konta. Spróbuj ponownie.');
            }
        }
    };

    // Funkcja do powrotu do pierwszego kroku (opcjonalna)
    const resetForm = () => {
        setIsEmailChecked(false);
        setIsEmailExists(false);
        setUsername('');
        setPassword('');
    };

    // Funkcja do ustalenia tekstu na przycisku
    const getButtonText = () => {
        if (!isEmailChecked) {
            return 'Kontynuuj';
        } else if (isEmailExists) {
            return 'Zaloguj się';
        } else {
            return 'Załóż konto organizatora';
        }
    };

    // Funkcja do ustalenia autoComplete dla pola hasła
    const getPasswordAutoComplete = () => {
        if (isEmailExists) {
            return 'current-password';
        } else {
            return 'new-password';
        }
    };

    const handlePasswordRecovery = async () => {
        if (isRecoveringPassword) {
            return;
        }

        const normalizedEmail = username.trim();

        if (!normalizedEmail) {
            showAlert('Wprowadź adres e-mail');
            return;
        }

        const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
        if (!isEmailValid) {
            showAlert('Wprowadź poprawny adres e-mail');
            return;
        }

        const isConfirmed = window.confirm(
            `Czy na pewno chcesz zresetować hasło? Wyślemy hasło tymczasowe na adres: ${normalizedEmail}`
        );

        if (!isConfirmed) {
            return;
        }

        setIsRecoveringPassword(true);
        try {
            await ApiService.resetPasswordByEmail(normalizedEmail);
            showAlert('Na podany adres e-mail przesłaliśmy nowe hasło dostępowe. Sprawdź swoją skrzynkę pocztową.');
        } catch (error) {
            console.error('Błąd resetu hasła:', error);
            showAlert('Nie udało się zresetować hasła. Spróbuj ponownie za chwilę.');
        } finally {
            setIsRecoveringPassword(false);
        }
    };

  return (
    <>
      {showRegistration && <OrganizerRegistration
          onBack={() => {
              dispatch(clearTokens());
              setShowRegistration(false);
              setShowLogin(true);
          }}
          onRegistrationComplete={handleRegistrationComplete}
      />}

      {showApprovalPending && (
          <div className="login-page">
              <div className="login-container approval-pending-container">
                  <h1 className="login-title">
                      <MapenLogo />
                  </h1>
                  <p className="login-subtitle">Weryfikacja profilu</p>
                  <div className="approval-pending-card">
                      <div className="approval-pending-badge">Status: Oczekiwanie na akceptację</div>
                      <p className="approval-pending-message">
                          Twój profil organizatora został zgłoszony i oczekuje na weryfikację przez administratora MAPEN. O decyzji powiadomimy Cię mailowo.
                      </p>
                      <div className="approval-pending-info">
                          Sprawdzamy zgłoszenia na bieżąco. Jeśli masz pytania, skontaktuj się z nami pod adresem{' '}
                          <a href="mailto:kontakt@mapenapp.com" className="approval-pending-contact-link">
                              kontakt@mapenapp.com
                          </a>.
                      </div>
                      <button
                          type="button"
                          className="login-button approval-pending-back-btn"
                          onClick={() => {
                              dispatch(clearTokens());
                              setShowApprovalPending(false);
                              resetForm();
                              setShowLogin(true);
                          }}
                      >
                          Wróć do logowania
                      </button>
                  </div>
              </div>
          </div>
      )}

        {showLogin && <div className="login-page">
          <div className="login-container">
            <h1 className="login-title">
                <MapenLogo />
            </h1>
            <p className="login-subtitle">Panel organizatora</p>
            <p className="login-instruction">Uzupełnij dane, aby kontynuować</p>

            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="username">Adres e-mail</label>
                <input
                  type="email"
                  id="username"
                  placeholder="Wpisz adres email"
                  value={username}
                  onChange={(e) => {
                      setIsEmailChecked(false);
                      setIsEmailExists(false);
                      setPassword('');
                      setUsername(e.target.value)
                  }}
                  autoComplete="email"
                />
              </div>

              {isEmailChecked && (
                <div className="form-group">
                    <label
                        htmlFor="password"
                        style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            gap: 12,
                        }}
                    >
                        <span>Hasło</span>

                        {isRegistrationPasswordContext && (
                            <span
                                className={[
                                    'password-hint',
                                    !isPasswordTouched
                                        ? 'password-hint--neutral'
                                        : isPasswordValidForRegistration
                                            ? 'password-hint--ok'
                                            : 'password-hint--warn',
                                ].join(' ')}
                            >
        {!isPasswordTouched || isPasswordValidForRegistration ? (
            // Wyświetla prosty komunikat, gdy pole nie jest ruszone lub hasło jest poprawne
            !isPasswordTouched
                ? 'Wymagania: 8 znaków, 1 wielka litera, 1 cyfra, 1 znak specjalny'
                : 'Hasło OK'
        ) : (
            // Wyświetla dynamiczną listę wymagań z kolorami
            <>
                Wymagania:
                <span className={isPasswordHasMinLength ? "pass-green" : "pass-orange"}> 8 znaków</span>,
                <span className={isPasswordHasUpperCase ? "pass-green" : "pass-orange"}> 1 wielka litera</span>,
                <span className={isPasswordHasNumber ? "pass-green" : "pass-orange"}> 1 cyfra</span>,
                <span className={isPasswordHasSpecialChar ? "pass-green" : "pass-orange"}> 1 znak specjalny</span>
            </>
        )}
    </span>
                        )}
                    </label>

                  <input
                    ref={passwordInputRef}
                    type="password"
                    id="password"
                    placeholder="Wpisz hasło"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={getPasswordAutoComplete()}
                  />
                    {isEmailExists && (
                        <div className="login-password-recovery-row">
                            <button
                                type="button"
                                className="login-password-recovery-btn"
                                onClick={handlePasswordRecovery}
                                disabled={isRecoveringPassword}
                            >
                                {isRecoveringPassword ? 'Wysyłanie nowego hasła...' : 'Nie pamiętasz hasła? Resetuj hasło!'}
                            </button>
                        </div>
                    )}
                    <div className="checkbox-container-terms">
                        <div className={!isPasswordValidForRegistration ? "disabled-terms" : ""} >
                          <label className="checkbox-terms disabled-terms" htmlFor="regulamin">
                            <input
                                type="checkbox"
                                id="regulamin"
                                name="regulamin"
                                checked={termsAccepted} // Powiązanie ze stanem
                                onChange={(e) => setTermsAccepted(e.target.checked)} // Aktualizacja stanu
                                />
                            <span className="checkbox-text">
                            Akceptuję <span className="terms-link" onClick={toggleModal}>regulamin</span>
                            </span>
                          </label>
                        </div>
                        <div className={!isPasswordValidForRegistration ? "disabled-terms" : ""} >
                          <label className="checkbox-terms disabled-terms" htmlFor="polityka">
                              <input
                                  type="checkbox"
                                  id="polityka"
                                  name="polityka"
                                  checked={privacyPolicyAccepted} // Powiązanie ze stanem
                                  onChange={(e) => setPrivacyPolicyAccepted(e.target.checked)} // Aktualizacja stanu
                              />
                              <span className="checkbox-text">
                              <a
                                href="https://mapenapp.com/polityka-prywatnosci/"
                                style={{ color: 'inherit', textDecoration: 'none' }}
                              >
                                Zapoznałem/-am się z Polityką prywatności
                              </a>
                              </span>
                          </label>
                        </div>

                        {/* Proste okno modalne */}
                        {isModalOpen && (
                            <div className="modal-overlay">
                                <div className="modal-content">
                                    <h2>Regulamin</h2>
                                    <div className="modal-body">
                                        <iframe
                                            title="Regulamin MAPEN"
                                            src="/Regulamin MAPEN-2.pdf"
                                            style={{ width: '100%', height: '70vh', border: 'none' }}
                                        />
                                    </div>
                                    <button onClick={() => setIsModalOpen(false)}>Zamknij</button>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={'left-text text-small'}>
                        Na start zaczynamy od prostych i potrzebnych rozwiązań dla organizatorów
                        wydarzeń
                        <ul>
                            <li>Publikujesz wydarzenia szybko i bez opłat</li>
                            <li>Twoje wydarzenia są prezentowane użytkownikom aplikacji</li>
                            <li>Otrzymujesz dostęp do podstawowych statystyk zainteresowania</li>
                        </ul>
                        Korzystanie z platformy jest obecnie w pełni bezpłatne.<br/>
                        Na początku koncentrujemy się na budowaniu społeczności i wspieraniu
                        organizatorów w dotarciu do uczestników. W przyszłości mogą pojawić się
                        dodatkowe funkcje płatne lub modele subskrypcyjne - o wszelkich zmianach
                        poinformujemy z wyprzedzeniem.<br/><br/>
                    </div>
                </div>
              )}

                <button
                    disabled={(isRegistrationPasswordContext && (!isPasswordValidForRegistration || !termsAccepted || !privacyPolicyAccepted))}
                    type="submit" className="login-button">
                    {getButtonText()}
                </button>
            </form>

          </div>
        </div>
        }
    </>
  );
}

export default Login;
